import { array, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { ApplicationState, CommanderThunkDispatch, LegalEntityIdentifier, WithCompanyId } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { notEmpty } from '@mews-commander/utils';
import {
    commanderForm,
    maxLegalIdentifierInputTextLength,
    validation,
    LEGAL_ENTITY_IDENTIFIERS,
} from '@mews-commander/legacy-form';
import { getGlobalLegalEntityIdentifiers } from '@mews-commander/core/legacy-globalization';
import { Entities } from '@mews-commander/legacy-entities/src/ducks';
import { updateContactInfo } from '../../actions';
import { getContactFormInitialValues } from '../../selectors';
import { ContactFormValues } from '../../types';
import {
    ContactFields,
    ContactFieldsProps,
    companyContactInfoFieldsConstraints,
    companyFiscalFieldsConstraints,
} from '../Fields';

interface StateProps {
    initialValues: ContactFormValues;
    legalEntityIdentifiersMap: Entities<LegalEntityIdentifier>;
}

type OwnProps = Pick<ContactFieldsProps, 'form'> & WithCompanyId & { readOnlyForm: boolean };

const getConstraints = (
    {
        legalEntityIdentifiersMap,
    }: {
        legalEntityIdentifiersMap: Entities<LegalEntityIdentifier>;
    },
    values: ContactFormValues
) => {
    let legalEntityIdentifiersConstraints = {};
    if (notEmpty(values[LEGAL_ENTITY_IDENTIFIERS])) {
        legalEntityIdentifiersConstraints = pipe(
            values[LEGAL_ENTITY_IDENTIFIERS],
            array.reduce(
                {} as Record<string, Record<string, string | unknown>>,
                (result, item: Record<string, string | null>) => {
                    const [key, value] = Object.entries(item)[0];
                    const isEmptyValue = value !== null ? pipe(value, string.trim, string.isEmpty) : true;
                    const format =
                        !isEmptyValue && legalEntityIdentifiersMap?.[key]?.validationRegex
                            ? {
                                  pattern: legalEntityIdentifiersMap?.[key]?.validationRegex,
                                  message: { textKey: TextKey.ValidateValue },
                              }
                            : null;
                    return {
                        ...result,
                        [key]: {
                            format,
                            length: maxLegalIdentifierInputTextLength,
                        },
                    };
                }
            )
        );
    }

    return {
        ...companyContactInfoFieldsConstraints,
        ...companyFiscalFieldsConstraints,
        [LEGAL_ENTITY_IDENTIFIERS]: {
            uniqueFieldsArray: legalEntityIdentifiersConstraints,
        },
    };
};

export default flow(
    commanderForm<ContactFormValues, ContactFieldsProps & Pick<StateProps, 'legalEntityIdentifiersMap'>>({
        enableReinitialize: true,
        validate: validation(getConstraints),
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        submitButtonProps: { variant: 'primary' },
    }),
    connect<StateProps, {}, OwnProps, ApplicationState>(
        (state, { companyId }) => ({
            initialValues: getContactFormInitialValues(state, { id: companyId }),
            legalEntityIdentifiersMap: getGlobalLegalEntityIdentifiers(state),
        }),
        (dispatch: CommanderThunkDispatch, { companyId }) => ({
            onSubmit: (values: ContactFormValues) => dispatch(updateContactInfo(values, companyId)),
        })
    )
)(ContactFields);
